import { ChangeDetectionStrategy, Component, computed, input, Signal } from '@angular/core';
import { HeaderDistruzioneOperazioniMassive, SummaryDocumentoToDestroy } from '../models/operazioni-massive.model';

@Component({
  selector: 'app-verbale-distruzione',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
      @for (page of pagedDocuments(); track idx; let idx = $index) {
        <app-verbale-distruzione-item [protocol]="protocol()" [header]="header()" [documents]="page" ></app-verbale-distruzione-item>
        <p-divider type="dashed" class="mt-2" />
      }
  `,
  styles: `
    .A4 { max-width:200mm; max-height:288mm; margin: 0 auto;}
  `
})
export class VerbaleDistruzioneComponent {

  protocol = input<string>('');
  header = input.required<HeaderDistruzioneOperazioniMassive>();
  documents = input<SummaryDocumentoToDestroy[]>([]);

  private readonly PAGE_SIZE = 1000;

public readonly pagedDocuments: Signal<SummaryDocumentoToDestroy[][]> = computed(() => {
  const result: SummaryDocumentoToDestroy[][] = [];

  // Ordino i documenti prima di fare il paging
  const sortedDocuments = [...this.documents()].sort((a, b) => {
    const [annoA, progAnnoA, progGiornoA] = this.parseNumeroDocumento(a.protocollo);
    const [annoB, progAnnoB, progGiornoB] = this.parseNumeroDocumento(b.protocollo);

    if (annoA !== annoB) return annoA - annoB;
    if (progAnnoA !== progAnnoB) return progAnnoA - progAnnoB;
    return progGiornoA - progGiornoB;
  });

  let outerIndex = 0;
  result.push([]);

  sortedDocuments.forEach((item, i) => {
    const currentPage = Math.trunc(i / this.PAGE_SIZE);
    if (currentPage > outerIndex) {
      result.push([]);
      outerIndex += 1;
    }
    result[outerIndex].push(item);
  });

  return result;
});

 parseNumeroDocumento(numero: string): [number, number, number] {
  const match = numero.match(/^(\d{4})[A-Z]+(\d{7})\/(\d+)$/);
  if (!match) {
    return [0, 0, 0];
  }
  const anno = parseInt(match[1], 10);
  const progressivoAnno = parseInt(match[2], 10);
  const progressivoGiorno = parseInt(match[3], 10);
  return [anno, progressivoAnno, progressivoGiorno];
}
}

@Component({
  selector: 'app-verbale-distruzione-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
      <div class="grid A4">
        @if( header().salvaSuRegistro) {
          <div class="col-12 mt-2">
            <div class="flex flex-column justify-content-center align-items-center gap-1 w-100">
              <img src="/assets/images/italia-emblema.png" width="80px" class="mb-1" />
              <span class="w-2"><hr /></span>
              <span class="font-italic">(TIMBRO LINEARE D'UFFICIO)</span>
              <span class="font-italic">ORGANO PERIFERICO DI SICUREZZA</span>
            </div>
          </div>

          <div class="col-12 text-sm mb-2">N. <span class="font-bold">{{protocol()}}</span> d'ordine di registrazione annuale del<br/>Registro dei verbali di distruzione dei documenti classificati</div>
          <div class="col-12 text-center mb-2"><h2>VERBALE <br/> DI DISTRUZIONE DI DOCUMENTI CLASSIFICATI</h2></div>
          <div class="col-12 mb-1">La sottonotata Commissione, nominata con ordine di servizio n. {{header().ordine}} in data {{header().dataDistruzione | date: 'dd/MM/yyyy'}}, dichiara di aver distrutto a mezzo {{header().mezzoDistruzione}} i documenti classificati elencati nel presente specchio.</div>
        }

        <div class="col-12">
          <p-table id="verbaleDistruzionePreview" [value]="documents()" styleClass="p-datatable-gridlines p-datatable-sm">
            <ng-template pTemplate="header">
                <tr>
                    <th class="text-center text-xs w-1">Qualifica e <br/>classifica di <br/>segretezza del <br/>documento</th>
                    <th class="text-center text-xs w-2">Numero di protocollo o di <br/>controllo del documento</th>
                    <th class="text-center text-xs w-3">Ente originatore <br/>del documento</th>
                    <th class="text-center text-xs w-1">Numero distintivo di <br/>copia del <br/>documento</th>
                    <th class="text-center text-xs w-1">Lingua in cui il <br/>documento è <br/>redatto</th>
                    <th class="text-center text-xs w-1">Numero corrispondente del registro di <br/>carico o (per le Pubblicazioni <br/>classificate) del tallone di carico</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
                <tr>
                    <td class="text-center text-sm">{{ item.classifica }} {{ item.qualifica }}</td>
                    <td class="text-center text-sm">{{ item.protocollo }}</td>
                    <td class="text-center text-sm">{{ item.ente }}</td>
                    <td class="text-center text-sm">{{ item.numeroDistintivoCopia }}</td>
                    <td class="text-center text-sm">{{ item.lingua }}</td>
                    <td class="text-center text-sm">{{ item.numeroOrdine }}</td>
                </tr>
            </ng-template>
          </p-table>
        </div>

        @if( header().salvaSuRegistro) {
          <div class="col-12 mt-1">
            <div class="flex flex-row justify-content-center align-content-center gap-1 w-100">
              <span>NOTE</span>
              <span class="w-screen"><hr /></span>
            </div>
          </div>

          <div class="col-12 mt-1">
            <div class="flex flex-row justify-content-center align-content-center gap-1">
              <span class="w-1"><hr /></span>
              <span>li</span>
              <span class="w-2"><hr /></span>
            </div>
          </div>

          <div class="col-12 mt-1">
            <div class="flex flex-column justify-content-center align-items-center gap-1 w-100">
              <span>LA COMMISSIONE</span>
              <span>Il presidente della commissione</span>
              <span class="w-2"><hr /></span>
            </div>
          </div>

          <div class="col-6 mt-1">
            <div class="flex flex-column justify-content-center align-items-center gap-1 w-100">
              <span>Testimone alla distruzione</span>
              <span class="w-4"><hr /></span>
            </div>
          </div>

          <div class="col-6 mt-1">
            <div class="flex flex-column justify-content-center align-items-center gap-1 w-100">
              <span>Testimone alla distruzione</span>
              <span class="w-4"><hr /></span>
            </div>
          </div>
        }
      </div>
`,
  styles: `
    .A4 { max-width:200mm; max-height:288mm; margin: 0 auto;}
  `
})
export class VerbaleDistruzioneItemComponent {

  protocol = input<string>('');
  header = input.required<HeaderDistruzioneOperazioniMassive>();
  documents = input<SummaryDocumentoToDestroy[]>([]);
}
